// Single source of truth for the Reports section.
//
// Adding a report = one entry here (+ its route in MainApp.js and component).
// The hub cards, the breadcrumb in SubNav, and the ⌘K command palette all read
// from this list, so reports stay in sync and the section never grows a
// "massive line across the top" — navigation flows through the hub instead.
//
// Fields:
//   slug      url segment under /reports ('' = the hub itself, omit from lists)
//   label     display name
//   code      legacy report code (526CGR etc.), shown as a chip; optional
//   desc      one-line summary for the hub card
//   category  grouping bucket on the hub
//   roles     optional role gate; omit to inherit the section's access

export const REPORT_CATEGORIES = [
  'Performance',
  'Customers',
  'Margin & GP',
  'Sales analysis',
  'Activity',
];

export const REPORTS = [
  // Performance
  { slug: 'branch',   label: 'Branch performance',  category: 'Performance',
    desc: "Today's sales vs target across branches." },
  { slug: 'area',     label: 'Area comparison',     category: 'Performance',
    desc: 'Sales by sales area for the current month.' },
  { slug: 'advisor',  label: 'Advisor performance', category: 'Performance',
    desc: 'Sales and margin per counter advisor.' },
  { slug: 'operator-daily', label: 'Operator daily sales', code: '312OPLL', category: 'Performance',
    desc: 'Per-day sales, credits and margin for each operator who keyed them.' },

  // Customers
  { slug: 'top-customers', label: 'Top customers by value', code: '4581RC', category: 'Customers',
    desc: 'Highest-spending accounts over a chosen period, with margin.' },
  { slug: 'customer',      label: 'Customer analysis',      category: 'Customers',
    desc: 'Spends, rebates, vouchers and six-month trend per customer.' },
  { slug: 'contacts',      label: 'Customer contacts',      category: 'Customers',
    desc: 'Logged calls, visits and messages against accounts.' },

  // Margin & GP
  { slug: 'gross-margin-by-rep', label: 'Gross margin by rep & customer', code: 'TRM44', category: 'Margin & GP',
    desc: 'True GP% broken down by rep and account for a period.' },

  // Sales analysis
  { slug: 'six-month-by-group', label: 'Six-month sales by product group', code: '526CGR', category: 'Sales analysis',
    desc: 'Monthly sales per customer and product group over six months.' },
  { slug: 'six-month-by-area',  label: 'Six-month customer sales by area', code: '526AC',  category: 'Sales analysis',
    desc: 'Monthly customer sales grouped by sales area over six months.' },
  { slug: 'sales',              label: 'Sales analysis',                   category: 'Sales analysis',
    desc: 'Invoice, daily, weekly and quotes summaries.' },

  // Activity
  { slug: 'bdm', label: 'BDM activity', category: 'Activity',
    desc: 'Field visit activity over the last 30 days.' },
];

// slug -> report (for the breadcrumb and any lookups). '' is intentionally absent.
const BY_SLUG = REPORTS.reduce((m, r) => { m[r.slug] = r; return m; }, {});

export const findReport = (slug) => BY_SLUG[slug] || null;

// Reports grouped by category, in REPORT_CATEGORIES order, dropping empty groups.
export const reportsByCategory = () =>
  REPORT_CATEGORIES
    .map(cat => ({ category: cat, items: REPORTS.filter(r => r.category === cat) }))
    .filter(g => g.items.length);
